import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { urlParameterCodes } from '@/constants/urlParams';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { BaseProduct } from '@/ui/services/BaseProductService';
import { Bundle } from '@/ui/services/BundleService';
import { useShallowRouter } from '../useShallowRouter';

export const useCurrentVariant = <TVariant extends ParsedProductVariant>(
  product: BaseProduct,
  updateURL?: boolean,
  handleSelectVariant?: (bundleProductInstanceId: string, variant: ParsedProductVariant) => void,
  selectedBundle?: Bundle,
): { currentVariant: TVariant; setSelectedVariant: (variant: TVariant) => void } => {
  // Initialize state with a generic variant type, to be casted later
  const [currentVariant, setCurrentVariant] = useState<TVariant>(
    product.getDefaultVariant() as TVariant,
  );

  const currentPath = usePathname();
  const shallowRouter = useShallowRouter();

  let setSelectedVariant: (selectedVariant: TVariant) => void;
  // eslint-disable-next-line prefer-const
  setSelectedVariant = (selectedVariant: TVariant) => {
    setCurrentVariant(selectedVariant);
    if (updateURL) {
      shallowRouter.replace(
        currentPath,
        { [urlParameterCodes.productDetail.sku]: selectedVariant.sku },
        true,
      );
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const skuFromURL = params.get(urlParameterCodes.productDetail.sku);
    let initialVariant: TVariant | null = null;

    if (skuFromURL) {
      const variant = product.getVariantBySKU(skuFromURL);
      if (variant) {
        initialVariant = variant as TVariant;
      }
    }

    initialVariant = initialVariant || (product.getDefaultVariant() as TVariant);

    if (initialVariant) {
      setSelectedVariant(initialVariant);
    }
  }, []);

  useEffect(() => {
    const bundleProduct = selectedBundle?.products.find((bProduct) => bProduct.id === product.id);
    // Add the current variant to selected product variants for the bundle
    if (bundleProduct) {
      handleSelectVariant?.(bundleProduct.bundleProductInstanceId, currentVariant);
    }
  }, [currentVariant, selectedBundle]);

  return { currentVariant, setSelectedVariant };
};
